import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface ProblemCarouselSectionProps {
  titleHighlight: string
  title: string
  body: string
  emphasis: string
  carouselCaption: string
  images: string[]
}

/**
 * Desktop slide width matches Figma 37:12502 (555px card). Track is left-clipped
 * via clip-path so cards bleed past the section's max-w to viewport-right.
 */
const SLIDE_W_DESKTOP = 555
const GAP = 16
const STEP_DESKTOP = SLIDE_W_DESKTOP + GAP

/**
 * #03 Problem + EBS documentary carousel.
 * Desktop: text LEFT, carousel RIGHT (50/50). Cards bleed rightward past the
 *   inner max-width into the viewport gutter. Infinite loop via cloned first
 *   slide + transition-end snap back.
 * Mobile: text TOP (center-aligned), carousel BELOW. Slides scroll-snap with
 *   24px left/right gutter; arrows wrap around for infinite navigation.
 * Figma: 37:12502 (desktop) / 194:6008 (mobile)
 */
export function ProblemCarouselSection({
  titleHighlight,
  title,
  body,
  emphasis,
  carouselCaption,
  images,
}: ProblemCarouselSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const total = images.length
  const [current, setCurrent] = useState(0)
  const [animate, setAnimate] = useState(true)
  const mobileScrollerRef = useRef<HTMLDivElement>(null)

  // Clone first slide at end so next() from last animates forward into a
  // duplicate, then snaps to index 0 without animation on transition end.
  const desktopSlides = [...images, images[0]]

  useEffect(() => {
    if (!animate) {
      const r = requestAnimationFrame(() => setAnimate(true))
      return () => cancelAnimationFrame(r)
    }
  }, [animate])

  // Sync `current` with the mobile scroller's snap position so prev/next
  // operate from the slide the user actually sees after swiping. Debounce
  // until scrolling settles so smooth-scroll intermediates don't override
  // a click-driven setCurrent.
  useEffect(() => {
    const sc = mobileScrollerRef.current
    if (!sc) return
    let timer: number | null = null
    const onScroll = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        timer = null
        const clamped = readMobileIndex()
        if (clamped < 0) return
        setCurrent((c) => (c % total === clamped ? c : clamped))
      }, 120)
    }
    sc.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      sc.removeEventListener('scroll', onScroll)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [total])

  const stepMobile = (): number => {
    const sc = mobileScrollerRef.current
    const slide = sc?.firstElementChild as HTMLElement | null
    if (!slide) return 296
    return slide.getBoundingClientRect().width + GAP
  }

  // Map the scroller's scrollLeft to a slide index. When the scroller is
  // pinned at its maximum scroll position, the last slide is in view even
  // though scrollLeft < (total - 1) * step — the trailing gap/padding plus
  // viewport width consumed the remaining travel. Without this end-clamp,
  // Math.round() rounds the last position down to total - 2 and the scroll
  // listener overwrites a click-driven setCurrent(total - 1) back one slide.
  const readMobileIndex = (): number => {
    const sc = mobileScrollerRef.current
    if (!sc) return -1
    // Mobile scroller is `lg:hidden` (display:none) on desktop — scrollLeft is
    // pinned at 0, so reading it would force base=0 and break prev/next. Fall
    // back to the desktop `current` state in that case.
    if (sc.clientWidth === 0) return -1
    const step = stepMobile()
    if (step <= 0) return -1
    const maxScroll = sc.scrollWidth - sc.clientWidth
    if (maxScroll > 0 && sc.scrollLeft >= maxScroll - 2) return total - 1
    const idx = Math.round(sc.scrollLeft / step)
    return Math.max(0, Math.min(total - 1, idx))
  }

  const visibleMobileIndex = (): number => {
    const idx = readMobileIndex()
    return idx < 0 ? current % total : idx
  }

  const scrollMobileTo = (idx: number) => {
    const sc = mobileScrollerRef.current
    if (!sc) return
    sc.scrollTo({ left: idx * stepMobile(), behavior: 'smooth' })
  }

  const prev = () => {
    const base = visibleMobileIndex()
    if (base === 0) {
      // Jump (no animation) to clone position past the end, then animate to last.
      setAnimate(false)
      setCurrent(total)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true)
          setCurrent(total - 1)
        })
      })
      scrollMobileTo(total - 1)
    } else {
      const nextIdx = base - 1
      setCurrent(nextIdx)
      scrollMobileTo(nextIdx)
    }
  }

  const next = () => {
    const base = visibleMobileIndex()
    // Allow current to reach `total` (the cloned first slide) only when wrapping
    // from the actual last slide; snap back on desktop transition end.
    if (base === total - 1) {
      setCurrent(total)
      scrollMobileTo(0)
    } else {
      setCurrent(base + 1)
      scrollMobileTo(base + 1)
    }
  }

  const handleTransitionEnd = () => {
    if (current === total) {
      setAnimate(false)
      setCurrent(0)
    }
  }

  return (
    <section
      id="problem"
      data-marketing-section=""
      className="w-full bg-qb-bg-tertiary overflow-x-clip"
    >
      <div className="mx-auto max-w-[1136px] py-20 lg:py-[100px]">
        <div
          data-testid="text-image-split"
          className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-10"
        >
          {/* Left: text — 50/50 split with carousel on desktop. Mobile is center-aligned. */}
          <div className="flex flex-col gap-10 px-6 lg:px-0 lg:flex-1 lg:min-w-0 text-center lg:text-left">
            <div className="flex flex-col gap-2">
              <p className="font-['Pretendard',sans-serif] font-bold text-[32px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-primary">
                {titleHighlight}
              </p>
              <p className="font-['Pretendard',sans-serif] font-bold text-[32px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-text-high whitespace-pre-line">
                {title}
              </p>
            </div>
            <div className="flex flex-col gap-10">
              <p className="font-['Pretendard',sans-serif] text-[18px] lg:text-[20px] font-normal leading-[1.5] tracking-[-0.6px] text-qb-text-mid whitespace-pre-line">
                {body}
              </p>
              <p className="font-['Pretendard',sans-serif] font-bold text-[22px] lg:text-[28px] leading-[1.4] tracking-[-0.6px] text-qb-text-high">
                {emphasis}
              </p>
            </div>
          </div>

          {/* Right: carousel — flex-1 with min-w-0 so the desktop track can clip overflow. */}
          <div className="lg:flex-1 lg:min-w-0 flex flex-col gap-6">
            {/* Mobile: scroll-snap horizontal track, 24px left/right gutter */}
            <div
              ref={mobileScrollerRef}
              className="flex lg:hidden gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none px-6 scroll-pl-6"
            >
              {images.map((src, i) => (
                <div
                  key={i}
                  className="relative snap-start shrink-0 w-[75vw] max-w-[280px] aspect-[555/296] rounded-[24px] overflow-hidden"
                >
                  <img
                    src={src}
                    alt={mt('media.documentaryImage', { number: i + 1 })}
                    className="w-full h-full object-cover"
                  />
                  <div
                    className={`absolute inset-0 bg-black/70 transition-opacity duration-200 ${i === current % total ? 'opacity-0' : 'opacity-100 delay-300'
                      }`}
                  />
                </div>
              ))}
            </div>

            {/* Desktop: translate-controlled track. clip-path extends rightward by 100vw
                so the next-slide peek uses ALL available right gutter space. */}
            <div
              className="hidden lg:block"
              style={{ clipPath: 'inset(0 -100vw 0 0)' }}
            >
              <div
                className="flex gap-4 will-change-transform"
                style={{
                  transform: `translateX(${-current * STEP_DESKTOP}px)`,
                  transition: animate ? 'transform 300ms ease' : 'none',
                }}
                onTransitionEnd={handleTransitionEnd}
              >
                {desktopSlides.map((src, i) => (
                  <div
                    key={i}
                    className="relative shrink-0 w-[555px] aspect-[555/296] rounded-[24px] overflow-hidden"
                  >
                    <img
                      src={src}
                      alt={mt('media.documentaryImage', { number: (i % total) + 1 })}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className={`absolute inset-0 bg-black/70 transition-opacity duration-200 ${i === current ? 'opacity-0' : 'opacity-100 delay-300'
                        }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Caption + prev/next — shared layout across breakpoints. */}
            <div className="flex items-center justify-between gap-4 px-6 lg:px-0 lg:pl-4">
              <p className="font-['Pretendard',sans-serif] font-medium text-[14px] lg:text-[16px] leading-[1.5] tracking-[-0.6px] text-qb-text-mid">
                {carouselCaption}
              </p>
              <div className="flex gap-3 lg:gap-4">
                <button
                  type="button"
                  onClick={prev}
                  aria-label={mt('ProblemCarouselSection.arialabel_359')}
                  className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-qb-gray-100 hover:bg-qb-gray-200 transition-colors flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5 text-qb-text-mid" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label={mt('ProblemCarouselSection.arialabel_360')}
                  className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-qb-gray-100 hover:bg-qb-gray-200 transition-colors flex items-center justify-center"
                >
                  <ChevronRight className="w-5 h-5 text-qb-text-mid" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
